import { BaseRepository } from './BaseRepository';
import { supabase } from '../supabase';

export interface AICall {
  id: string;
  from_number: string;
  to_number: string;
  status: 'in_progress' | 'completed' | 'transferred' | 'failed';
  started_at: string;
  ended_at?: string;
  duration_seconds?: number;
  company_id?: string;
  created_at?: string;
  company_configs?: {
    name: string;
    is_active: boolean;
  };
}

export interface AICallStats {
  totalCalls: number;
  activeCalls: number;
  completedCalls: number;
  transferredCalls: number;
  avgCallDuration: number;
  transferRate: number;
}

export class AICallRepository extends BaseRepository<AICall> {
  constructor() {
    super({ tableName: 'ai_calls', primaryKey: 'id' });
  }

  /**
   * Get all calls with status and duration data
   */
  async getAllCallsWithStats(): Promise<Partial<AICall>[]> {
    const { data, error } = await supabase
      .from(this.getTableName())
      .select('id, status, duration_seconds, created_at')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  /**
   * Get recent calls with company information
   */
  async getRecentCallsWithCompany(limit: number = 10): Promise<any[]> {
    const { data, error } = await supabase
      .from(this.getTableName())
      .select(`
        id,
        from_number,
        to_number,
        status,
        started_at,
        ended_at,
        duration_seconds,
        company_configs!inner(name, is_active)
      `)
      .eq('company_configs.is_active', true)
      .not('status', 'is', null)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }

  /**
   * Calculate call statistics
   */
  async getCallStats(): Promise<AICallStats> {
    const calls = await this.getAllCallsWithStats();

    const completedCalls = calls.filter(call => call.status === 'completed');
    const transferredCalls = calls.filter(call => call.status === 'transferred');
    const activeCalls = calls.filter(call => call.status === 'in_progress');

    const totalDuration = completedCalls.reduce(
      (sum, call) => sum + (call.duration_seconds || 0),
      0
    );

    const avgDuration = completedCalls.length > 0 
      ? totalDuration / completedCalls.length 
      : 0;

    const transferRate = calls.length > 0 
      ? (transferredCalls.length / calls.length) * 100 
      : 0;

    return {
      totalCalls: calls.length,
      activeCalls: activeCalls.length,
      completedCalls: completedCalls.length,
      transferredCalls: transferredCalls.length,
      avgCallDuration: Math.round(avgDuration),
      transferRate: Math.round(transferRate * 100) / 100
    };
  }

  /**
   * Get calls by status
   */
  async getCallsByStatus(status: AICall['status']): Promise<AICall[]> {
    const { data, error } = await supabase
      .from(this.getTableName())
      .select('*')
      .eq('status', status)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }
}
